// DashboardScreen — director home (docs/miniapp-m3-tz.md §3.4).
// Faithful to the design bundle's DirectorDashboard, on the live
// GET /api/miniapp/dashboard response: period segmented control, 2×2 KPI grid
// with WoW delta-chips, tenant trend sparkline, worst-3 / best-3 manager cards,
// optional AI digest. Critical-deals preview arrives with the next M-3 slice.
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchDashboard } from '../../api/director'
import { useAiFeatures } from '../../api/features'
import { LastUpdated } from '../../components/LastUpdated'
import { ReactivationEntryCard } from '../../components/ReactivationEntryCard'
import { ReportsArchiveEntryCard } from '../../components/ReportsArchiveEntryCard'
import type {
  DashboardManagerCard,
  DashboardPeriod,
  DashboardResponse,
  KpiValue,
} from '../../api/types'
import {
  Avatar,
  Button,
  Icon,
  ScreenSkeleton,
  SectionHeader,
  Sparkline,
  t,
} from '../../design'

const PERIODS: { id: DashboardPeriod; labelKey: string }[] = [
  { id: 'today', labelKey: 'per.day' },
  { id: 'week', labelKey: 'per.week' },
  { id: 'month', labelKey: 'per.month' },
]

function Segmented({ value, onChange }: { value: DashboardPeriod; onChange: (p: DashboardPeriod) => void }) {
  return (
    <div className="row" style={{ gap: 2, background: 'var(--surface-2)', borderRadius: 10, padding: 2 }}>
      {PERIODS.map((p) => (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          className="t-caption-s"
          style={{
            border: 'none', cursor: 'pointer', padding: '6px 10px', borderRadius: 8,
            background: value === p.id ? 'var(--surface)' : 'transparent',
            color: value === p.id ? 'var(--text-1)' : 'var(--text-3)',
            fontWeight: value === p.id ? 600 : 400,
            boxShadow: value === p.id ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          {t(p.labelKey)}
        </button>
      ))}
    </div>
  )
}

// WoW delta chip. `positiveIsGood` flips the color (overdue up = bad).
function DeltaChip({ delta, positiveIsGood }: { delta: number | null; positiveIsGood: boolean }) {
  if (delta == null || delta === 0) return null
  const up = delta > 0
  const good = up === positiveIsGood
  const color = good ? 'var(--success)' : 'var(--danger)'
  const bg = good ? 'var(--success-bg)' : 'var(--danger-bg)'
  const Arrow = up ? Icon.ArrowUp : Icon.ArrowDown
  const num = Math.abs(delta)
  return (
    <span className="t-caption-s" style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, color, background: bg,
      borderRadius: 100, padding: '1px 6px', fontVariantNumeric: 'tabular-nums',
    }}>
      <Arrow size={11} sw={2.75} />{Number.isInteger(num) ? num : num.toFixed(1)}
    </span>
  )
}

function KpiTile({
  klass, value, label, kpi, positiveIsGood,
}: { klass: string; value: string; label: string; kpi: KpiValue; positiveIsGood: boolean }) {
  return (
    <div className={`kpi ${klass}`}>
      <div className="row middle gap-2">
        <span className="kpi-num">{value}</span>
        <DeltaChip delta={kpi.delta_wow} positiveIsGood={positiveIsGood} />
      </div>
      <span className="kpi-label">{label}</span>
    </div>
  )
}

function ManagerCard({ m, onClick }: { m: DashboardManagerCard; onClick: () => void }) {
  const name = m.name || `#${m.manager_id}`
  return (
    <div
      className="card card-16"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick() }}
      style={{ opacity: m.inactive ? 0.6 : 1, cursor: 'pointer' }}
    >
      <div className="row middle gap-3">
        <Avatar name={name} size={36} />
        <div className="flex-1" style={{ minWidth: 0 }}>
          <div className="t-body-s ellipsis">{name}</div>
          <div className="t-caption">
            {m.open} откр. · <span style={{ color: m.overdue ? 'var(--danger)' : undefined }}>{m.overdue} проср.</span>
            {m.avg_score != null && <> · {m.avg_score.toFixed(1)} ★</>}
          </div>
        </div>
        <DeltaChip delta={m.delta_overdue} positiveIsGood={false} />
        <Icon.ArrowRight size={16} sw={2.25} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
      </div>
    </div>
  )
}

export function DashboardScreen() {
  const navigate = useNavigate()
  const features = useAiFeatures()
  const [period, setPeriod] = useState<DashboardPeriod>('today')
  const { data, isLoading, isError, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['dashboard', period],
    queryFn: () => fetchDashboard(period),
    // The director's dashboard must never show yesterday's numbers: refetch on
    // every open (realtime team.load_changed keeps it live while mounted, but
    // Telegram's webview focus events are unreliable, so don't rely on them).
    refetchOnMount: 'always',
  })

  const openManager = (managerId: number) => navigate(`/team/${managerId}`)

  if (isLoading) return <ScreenSkeleton kind="today" />

  if (isError || !data) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 14, padding: 24 }}>
        <span style={{ width: 72, height: 72, borderRadius: 18, background: 'var(--surface-2)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.CloudOff size={30} sw={2} />
        </span>
        <div className="t-h2">{t('task.loadError')}</div>
        <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
      </div>
    )
  }

  const d: DashboardResponse = data
  const sparkData = d.tenant_trend.points.map((p) => p.value ?? 0)

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="row between middle" style={{ marginBottom: 8 }}>
        <div className="t-display">{t('dir.team')}</div>
        <Segmented value={period} onChange={setPeriod} />
      </div>

      {/* Freshness + manual refresh — the dashboard otherwise drifts silently. */}
      <div className="row between middle" style={{ marginBottom: 12, minHeight: 20 }}>
        <LastUpdated at={dataUpdatedAt} />
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label={t('btn.refresh')}
          style={{
            border: 'none', background: 'transparent', cursor: isFetching ? 'default' : 'pointer',
            color: 'var(--text-2)', padding: 4, display: 'inline-flex', alignItems: 'center', gap: 4,
            opacity: isFetching ? 0.5 : 1,
          }}
        >
          <Icon.Refresh size={15} sw={2} />
          <span className="t-caption-s">{t('btn.refresh')}</span>
        </button>
      </div>

      {/* KPI 2×2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <KpiTile klass="kpi-brand" value={String(d.kpi.open_tasks.value)} label={t('dir.tasks')} kpi={d.kpi.open_tasks} positiveIsGood />
        <KpiTile klass="kpi-danger" value={String(d.kpi.overdue.value)} label={t('dir.overdue')} kpi={d.kpi.overdue} positiveIsGood={false} />
        <KpiTile klass="kpi-success" value={String(d.kpi.completed.value)} label={t('dir.done')} kpi={d.kpi.completed} positiveIsGood />
        <KpiTile klass="kpi-ai" value={d.kpi.avg_score.value ? d.kpi.avg_score.value.toFixed(1) : '—'} label={t('dir.avgScore')} kpi={d.kpi.avg_score} positiveIsGood />
      </div>

      <ReactivationEntryCard />
      <ReportsArchiveEntryCard />

      {/* Tenant trend sparkline */}
      {sparkData.some((v) => v > 0) && (
        <div className="card card-16" style={{ marginBottom: 12 }}>
          <div className="row between middle" style={{ marginBottom: 6 }}>
            <span className="t-label">{t('dir.overdue')} · 14 {t('unit.d')}</span>
          </div>
          <Sparkline data={sparkData} color="var(--danger)" width={300} height={36} />
        </div>
      )}

      {/* AI digest — director can hide this card for the tenant */}
      {features.ai_digest && d.ai_digest?.text && (
        <div className="aicard" style={{ marginBottom: 12 }}>
          <div className="row middle gap-2">
            <Icon.Sparkle size={18} style={{ color: 'var(--ai)' }} />
            <span className="t-label" style={{ color: 'var(--ai)' }}>{t('dir.aiDigest')}</span>
          </div>
          <div className="t-caption" style={{ marginTop: 4, color: 'var(--text-1)', whiteSpace: 'pre-line' }}>{d.ai_digest.text}</div>
        </div>
      )}

      {/* Worst-3 */}
      {d.worst_3.length > 0 && (
        <>
          <SectionHeader tone="danger">{t('dir.worst3')}</SectionHeader>
          <div className="col gap-2">
            {d.worst_3.map((m) => <ManagerCard key={m.manager_id} m={m} onClick={() => openManager(m.manager_id)} />)}
          </div>
        </>
      )}

      {/* Best-3 */}
      {d.best_3.length > 0 && (
        <>
          <SectionHeader>{t('dir.best3')}</SectionHeader>
          <div className="col gap-2">
            {d.best_3.map((m) => <ManagerCard key={m.manager_id} m={m} onClick={() => openManager(m.manager_id)} />)}
          </div>
        </>
      )}

      {d.summary.total_managers === 0 && (
        <div className="t-caption" style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-3)' }}>
          {t('dir.noManagers')}
        </div>
      )}
    </div>
  )
}
